import { DataEndChannel } from 'src/app/project/shared/interfaces/data-end-channel.interface';
import {
  SchemaComponentInfo,
  SchemaDeviceInfo,
  SchemaRouteStationInfo
} from 'src/app/project/project-schema/shared/interfaces/schema-route-station-info.interface';

/**
 * Sammelt alle Kanal-Kontexte (`cInfo`/`cInfos`) der Schema-Komponenten einer
 * Station in einer flachen Liste – unabhängig davon, ob die Komponente ein
 * Einzel- (`cInfo`), Mehrfach- (`cInfos`) oder Listen-Element ist.
 */
export function collectComponentInfos(schemaInfo?: SchemaRouteStationInfo | null): SchemaComponentInfo[] {
  if (!schemaInfo) { return []; }
  const infos: (SchemaComponentInfo | null | undefined)[] = [
    schemaInfo.gps?.cInfo,
    ...(schemaInfo.solar?.cInfos ?? []),
    schemaInfo.sim?.cInfo,
    ...(schemaInfo.digitalIOs ?? []).map(d => d.cInfo),
    ...(schemaInfo.displays ?? []).map(d => d.cInfo),
    schemaInfo.lamp?.cInfo,
    ...(schemaInfo.radars ?? []).map(r => r.cInfo),
    schemaInfo.fuelcell?.cInfo,
    ...(schemaInfo.cameras ?? []).map(c => c.cInfo),
    schemaInfo.barrier?.cInfo,
    schemaInfo.weather?.cInfo
  ];
  return infos.filter((info): info is SchemaComponentInfo => !!info?.channel);
}

/** True, wenn mindestens ein Feld der Geräteinformationen einen Wert enthält. */
export function hasDeviceInfoValues(device?: SchemaDeviceInfo | null): boolean {
  if (!device) { return false; }
  return [device.deviceName, device.serialNumber, device.version].some(v => v !== null && v !== undefined && v !== '');
}

/**
 * Geräteinformationen zu einem DataEndChannel: Zuordnung über (fg, de) zum
 * Kanal-Kontext der Schema-Komponente. Liefert null, wenn kein Gerät bekannt ist
 * oder alle Felder leer sind.
 */
export function getChannelDeviceInfo(channel: DataEndChannel, schemaInfo?: SchemaRouteStationInfo | null): SchemaDeviceInfo | null {
  const match = collectComponentInfos(schemaInfo)
    .find(info => info.channel.fg === channel.fg && info.channel.de === channel.de);
  return hasDeviceInfoValues(match?.device) ? match!.device! : null;
}

/** Eine Zeile der Geräteinformationen (Beschriftung + Wert) für die Anzeige. */
export interface DeviceInfoRow {
  key: keyof SchemaDeviceInfo;
  label: string;
  value: string;
}

/** Nur befüllte Felder, in fester Anzeigereihenfolge. */
export function toDeviceInfoRows(device: SchemaDeviceInfo): DeviceInfoRow[] {
  const rows: DeviceInfoRow[] = [
    { key: 'deviceName', label: 'Gerätename', value: device.deviceName ?? '' },
    { key: 'serialNumber', label: 'Seriennummer', value: device.serialNumber ?? '' },
    { key: 'version', label: 'Version', value: device.version ?? '' }
  ];
  return rows.filter(r => r.value !== '');
}

/** Alle Zeilen als Text (`Beschriftung: Wert`, zeilenweise) – für „Alles kopieren". */
export function deviceInfoRowsToText(rows: DeviceInfoRow[]): string {
  return rows.map(r => `${r.label}: ${r.value}`).join('\n');
}
